import { extensionsConfig } from "client/config/extensionsConfig";
import { useDeviceUpload } from "client/hooks/upload/useDeviceUpload";
import { useGoogleDriveUpload } from "client/hooks/upload/useGoogleDriveUpload";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { UploadIcon } from "components/common/Icons/UploadIcon";
import { List } from "components/common/List/List";
import { Popup } from "components/common/Popup/Popup";
import { Translate } from "components/common/Translate/Translate";
import { DeviceUpload } from "components/common/Upload/DeviceUpload/DeviceUpload";
import { DropboxUpload } from "components/common/Upload/DropboxUpload/DropboxUpload";
import { GoogleDriveAccessErrorModal } from "components/common/Upload/GoogleDriveUpload/GoogleDriveAccessErrorModal/GoogleDriveAccessErrorModal";
import { GoogleDriveUpload } from "components/common/Upload/GoogleDriveUpload/GoogleDriveUpload";
import { UrlUpload } from "components/common/Upload/UrlUpload/UrlUpload";
import { useUploadImage } from "./useUploadImage";

const COLOR_PICKER_FILE_INPUT_ID = "color-picker-file-input";

const supportedExtensions = extensionsConfig["color-picker"];

export const UploadImage = () => {
    const { isOpen, toggle, close, handleFile, handleUploadStart, handleClick } = useUploadImage();
    const { handleClick: handleDeviceUpload } = useDeviceUpload(
        handleFile,
        { allowMultiple: false, supportedExtensions },
        COLOR_PICKER_FILE_INPUT_ID,
    );
    const {
        handleClick: handleGoogleDriveUploadClick,
        accessError,
        setAccessError,
        isLoading: isGoogleDriveLoading,
    } = useGoogleDriveUpload(handleFile, { allowMultiple: false, supportedExtensions }, isOpen);

    return (
        <>
            <GoogleDriveAccessErrorModal
                isOpen={accessError}
                close={() => setAccessError(false)}
                handleRetry={handleGoogleDriveUploadClick}
            />
            <Popup
                isOpen={isOpen}
                close={close}
                content={
                    <div className="absolute left-0 right-0 bg-white dark:bg-darkSurface-200 flex flex-col gap-3 mx-4 py-2 font-xl rounded-xl border border-irGray-300 dark:border-darkSurface-400 my-2">
                        <List
                            items={{
                                from_device: (
                                    <DeviceUpload
                                        handleFile={handleFile}
                                        htmlFileInputId={COLOR_PICKER_FILE_INPUT_ID}
                                        options={{
                                            allowMultiple: false,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                    />
                                ),
                                dropbox: (
                                    <DropboxUpload
                                        handleFile={handleFile}
                                        options={{
                                            allowMultiple: false,
                                            uploadStart: handleUploadStart,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                        isOpen={isOpen}
                                    />
                                ),
                                google_drive: (
                                    <GoogleDriveUpload
                                        options={{
                                            allowMultiple: false,
                                            uploadStart: handleUploadStart,
                                            onClick: () => {
                                                handleClick();
                                                handleGoogleDriveUploadClick();
                                            },
                                            supportedExtensions,
                                        }}
                                        isLoading={isGoogleDriveLoading}
                                    />
                                ),
                                url_upload: (
                                    <UrlUpload
                                        handleFile={handleFile}
                                        options={{
                                            uploadStart: handleUploadStart,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </div>
                }
            >
                <div className="cursor-pointer mx-4 flex items-center bg-irBlue-200 dark:bg-darkSurface-200 text-white rounded-xl font-semibold overflow-hidden w-full">
                    <div
                        className="flex gap-4 border-r border-r-white dark:border-r-darkSurface-400 dark:hover:bg-darkSurface-300 py-3 w-full items-center justify-center"
                        onClick={() => {
                            handleClick();
                            handleDeviceUpload();
                        }}
                        data-testid={`device-upload`}
                    >
                        <div className="">
                            <UploadIcon className="fill-white" />
                        </div>
                        <div className="select-none capitalize">
                            <Translate keyName="upload-image" />
                        </div>
                    </div>
                    <div
                        className={`flex items-center justify-center py-4 px-4 ${
                            isOpen ? `bg-irBlue-400 dark:bg-darkSurface-200` : ""
                        } dark:hover:bg-darkSurface-300`}
                        onClick={toggle}
                        data-testid={`upload-list-opener`}
                    >
                        {isOpen ? (
                            <div className="rotate-180">
                                <ArrowIcon className="stroke-white" height={15} width={15} />
                            </div>
                        ) : (
                            <div className="">
                                <ArrowIcon className="stroke-white" height={15} width={15} />
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </>
    );
};
